import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNOK, formatPercent } from "@/lib/format";
import type {
  YearlyPersonBf,
  YearlyPersonAccuracy,
  SingleSaleRecord,
  CustomerBfRecord,
} from "@/lib/dashboard-halloffame-calc";
import type { ProjectMeta } from "@/hooks/use-sales-dashboard-data";

interface HallOfFamePanelProps {
  bestYearlyBf: YearlyPersonBf[];
  bestAccuracy: YearlyPersonAccuracy[];
  biggestSales: SingleSaleRecord[];
  bestCustomers: CustomerBfRecord[];
  nameOf: (userId: string) => string;
  customerNameOf: (customerId: string) => string;
  projectMetaOf: (projectId: string) => ProjectMeta | undefined;
  isLoading?: boolean;
}

const MEDAL = ["🥇", "🥈", "🥉"];

interface Row {
  key: string;
  label: string;
  value: string;
}

/** A single Hall of Fame category card with a top-3 list. */
function CategoryCard({ title, rows, isLoading }: { title: string; rows: Row[]; isLoading?: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-24 animate-pulse rounded bg-muted" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen data ennå.</p>
        ) : (
          <ol className="space-y-1">
            {rows.map((r, i) => (
              <li key={r.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 truncate">
                  <span className="w-5 text-center">{MEDAL[i] ?? i + 1}</span>
                  <span className="truncate">{r.label}</span>
                </span>
                <span className="tabular-nums">{r.value}</span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * F3.2 Hall of Fame — all-time records (ignores the period filter). Four
 * categories with room for 2 more later: highest yearly BF, best yearly budget
 * precision, biggest single sale, best customer.
 */
export function HallOfFamePanel({
  bestYearlyBf,
  bestAccuracy,
  biggestSales,
  bestCustomers,
  nameOf,
  customerNameOf,
  projectMetaOf,
  isLoading,
}: HallOfFamePanelProps) {
  const bfRows: Row[] = bestYearlyBf.map((r) => ({
    key: `${r.userId}-${r.year}`,
    label: `${nameOf(r.userId)} · ${r.year}`,
    value: formatNOK(r.bfKr),
  }));

  const accuracyRows: Row[] = bestAccuracy.map((r) => ({
    key: `${r.userId}-${r.year}`,
    label: `${nameOf(r.userId)} · ${r.year}`,
    value: formatPercent(r.deviationPct),
  }));

  const salesRows: Row[] = biggestSales.map((r) => {
    const meta = projectMetaOf(r.projectId);
    const name = meta ? (meta.code ? `${meta.code} · ${meta.name}` : meta.name) : "Ukjent prosjekt";
    return { key: r.projectId, label: `${name} (${r.year})`, value: formatNOK(r.bfActualKr) };
  });

  const customerRows: Row[] = bestCustomers.map((r) => ({
    key: r.customerId,
    label: customerNameOf(r.customerId),
    value: formatNOK(r.bfKr),
  }));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <CategoryCard title="Høyeste GEEK (år)" rows={bfRows} isLoading={isLoading} />
      <CategoryCard title="Beste budsjettering (år)" rows={accuracyRows} isLoading={isLoading} />
      <CategoryCard title="Største enkeltsalg" rows={salesRows} isLoading={isLoading} />
      <CategoryCard title="Beste kunde" rows={customerRows} isLoading={isLoading} />
    </div>
  );
}
